import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Box,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import ManufacturerDropdown from './Components/ManufacturerDropdown';
import { LinkedManufacturesAction, MyBenefitsAction } from './APIs/action';
import { toast } from 'react-toastify';
import SocialSecurityBenefit from './Components/SSB';
import { BenefitType } from './constants';
import { trackAnalyticsEvent } from '../../../utils/clevertap';
import { useSelector } from 'react-redux';
import StickyEntity from '../../../v3/Common/Entity/StickyEntity';
import AnnualBenefitCard from "../../../v3/Benefits/AnnualTrip/Components/AnnualBenefitCard";
import { HumbeeTabs } from '../../MUI_custom_component';
import { MODULE_PERMISSION_ENUM } from '../../../v3/Sidebar/Enum';
import NoAccess from '../../../v3/Common/NoAccess/NoAccess';
import { getSelectedEntityStatus } from '../../../v3/Reducers/Entity/EntityReducer';
import { STATUS } from '../../../v3/Common/Entity/Enums';

const MyBenefits = () => {
  // Get manufacturer and entity from Redux via Entity component
  const {
    selectedSupplierData,
    selectedSupplier: selectedManufacturer,
    selectedEntity: selectedCompanyEntity,
    selectedEntityInfo,
  } = useSelector(
    (state) => state.entityReducer
  );
  const hasAccess = selectedEntityInfo?.has_access;
  const location = useLocation();

  const [benefitData, setBenefitData] = useState([]);
  const [socialSecurityBenefits, setSocialSecurityBenefits] = useState({});
  const [annualBenefits, setAnnualBenefits] = useState({});
  const [currentTab, setCurrentTab] = useState(0);
  const [loader, setLoader] = useState(false);
  const lastFetchedKeyRef = useRef(null);

  const tabData = benefitData.filter(
    (benefit) =>
      benefit.display_name === BenefitType.SOCIAL_SECURITY_BENEFIT ||
      (benefit.display_name === BenefitType.ANNUAL_BENEFIT && benefit?.benefits?.length > 0)
  );

  useEffect(() => {
    if (tabData.length === 0) return;
    if (location.state?.selectedTab) {
      const idx = tabData.findIndex((b) => b.display_name === location.state.selectedTab);
      setCurrentTab(idx >= 0 ? idx : 0);
    } else {
      setCurrentTab(0);
    }
  }, [socialSecurityBenefits]);
  const getMyBenefitData = async (
    selectedManufacturer,
    selectedCompanyEntity,
  ) => {
    setLoader(true);
    // Clear previous data when changing manufacturer
    setBenefitData([]);
    setSocialSecurityBenefits({});
    setAnnualBenefits({});
    setCurrentTab(0);

    const { data, message, error } = await MyBenefitsAction(
      selectedManufacturer,
      selectedCompanyEntity,
    );
    setLoader(false);

    if (!error) {
      if (data && data.length > 0) {
        setBenefitData(data);
        const socialSecurity = data.find(
          (benefit) =>
            benefit.display_name === BenefitType.SOCIAL_SECURITY_BENEFIT,
        );
        setSocialSecurityBenefits(socialSecurity || {});
        const annual = data.find(
          (benefit) => benefit.display_name === BenefitType.ANNUAL_BENEFIT,
        );
        setAnnualBenefits(annual || {});
      } else {
        setBenefitData([]);
        setSocialSecurityBenefits({});
        setAnnualBenefits({});
      }
    } else {
      setBenefitData([]);
      setSocialSecurityBenefits({});
      setAnnualBenefits({});
      toast.error(message);
    }
  };

  useEffect(() => {
    if (!selectedManufacturer || !selectedCompanyEntity || hasAccess === false) {
      return;
    }

    const fetchKey = `${selectedManufacturer}::${selectedCompanyEntity}`;
    if (lastFetchedKeyRef.current === fetchKey) {
      return;
    }
    lastFetchedKeyRef.current = fetchKey;

    getMyBenefitData(selectedManufacturer, selectedCompanyEntity);
  }, [selectedManufacturer, selectedCompanyEntity, hasAccess]);

  useEffect(() => {
    if (selectedManufacturer && benefitData.length > 0) {
      trackAnalyticsEvent('MY_BENEFIT_BENEFIT_COMPARISON_LOAD', {
        manufacturer_id: selectedManufacturer,
      });
    }
  }, [selectedManufacturer, benefitData]);

  const handleTabChangeWithTracking = (_, newValue) => {
    setCurrentTab(newValue);

    // Track tab selection event
    const benefit_type = tabData[newValue]?.display_name;
    if (benefit_type && selectedManufacturer) {
      trackAnalyticsEvent('MY_BENEFIT_TAB_CHANGE', {
        manufacturer_id: selectedManufacturer,
        benefit_type,
      });
    }
  };
  const isEntityEnabled = selectedSupplierData?.entity_enabled;
  
  const marginTop = selectedEntityInfo?.status === STATUS.INACTIVE && selectedEntityInfo?.has_history === false
  return (
    <>
      <StickyEntity module_name={MODULE_PERMISSION_ENUM.MY_BENEFITS}  />
      {hasAccess === false ?(
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
          }}
        >
          <NoAccess />
        </Box>
      ) : (
      <Box sx={{ mt: { xs: !marginTop ? '96px' : 0, md: 0 }, p: 2 }}>
        {loader ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {tabData.length > 0 ? (
              <HumbeeTabs
                data={tabData}
                currentTab={currentTab}
                onChange={(_, newValue) => {
                  if (newValue !== null) handleTabChangeWithTracking(_, newValue);
                }}
                getTabLabel={(item) => item.display_name}
                getTabKey={(item) => item.display_name}
                tabType="dashed"
                tabsSx={{ mb: 3 }}
                renderTabContent={(item) => {
                  if (item.display_name === BenefitType.SOCIAL_SECURITY_BENEFIT) {
                    return socialSecurityBenefits && Object.keys(socialSecurityBenefits).length > 0 ? (
                      <SocialSecurityBenefit
                        benefits={socialSecurityBenefits}
                        selectedManufacturer={selectedManufacturer}
                        selectedCompanyEntity={selectedCompanyEntity}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                      </Box>
                    );
                  }
                  if (item.display_name === BenefitType.ANNUAL_BENEFIT) {
                    return annualBenefits && Object.keys(annualBenefits).length > 0 ? (
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {annualBenefits.benefits.map((benefit, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <AnnualBenefitCard item={benefit} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                        <CircularProgress />
                      </Box>
                    );
                  }
                }}
              />
            ) : (
              <p>No tabs available</p>
            )}
          </>
        )}
      </Box>
      )}
    </>
  );
};

export default MyBenefits;
